/**
 * SM-2 Spaced Repetition Algorithm
 *
 * Pure functions for calculating spaced repetition intervals.
 * Based on the SuperMemo SM-2 algorithm.
 */

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface SM2Input {
  quality: number; // 0-5 rating
  repetitions: number;
  ease_factor: number;
  interval_days: number;
}

export interface SM2Output {
  repetitions: number;
  ease_factor: number;
  interval_days: number;
  next_review_at: string; // ISO timestamp
}

/** UX-simplified rating buttons */
export type SRSRating = "again" | "good" | "easy";

/* ------------------------------------------------------------------ */
/*  SM-2 Algorithm                                                     */
/* ------------------------------------------------------------------ */

/**
 * SM-2 spaced repetition algorithm.
 * Pure function — no side effects, easy to test.
 *
 * @param input - Current card state and quality rating (0-5)
 * @returns New card state with updated interval and next review date
 */
export function sm2(input: SM2Input): SM2Output {
  const { quality, repetitions, ease_factor, interval_days } = input;

  let newRepetitions: number;
  let newInterval: number;
  let newEaseFactor: number;

  // Calculate new ease factor (minimum 1.3)
  newEaseFactor = Math.max(
    1.3,
    ease_factor + 0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02)
  );

  if (quality >= 3) {
    // Correct response
    if (repetitions === 0) {
      newInterval = 1;
    } else if (repetitions === 1) {
      newInterval = 6;
    } else {
      newInterval = Math.round(interval_days * newEaseFactor);
    }
    newRepetitions = repetitions + 1;
  } else {
    // Incorrect — reset
    newRepetitions = 0;
    newInterval = 1;
  }

  // Calculate next review date
  const now = new Date();
  const next = new Date(now.getTime() + newInterval * 24 * 60 * 60 * 1000);

  return {
    repetitions: newRepetitions,
    ease_factor: Math.round(newEaseFactor * 100) / 100,
    interval_days: newInterval,
    next_review_at: next.toISOString(),
  };
}

/** Map UX button to SM-2 quality rating */
export function ratingToQuality(rating: SRSRating): number {
  switch (rating) {
    case "again":
      return 1;
    case "good":
      return 3;
    case "easy":
      return 5;
  }
}
